<template>
  <div class="main-body" ref="mainBody">
    <div class="title-bar">
      <h1>Leads</h1>
      <div class="actions">
        <div class="primary-input" style="width: 25rem">
          <input type="search" placeholder="Search..." class="no-outline" v-model="searchText" @input="updateFilters()" />
          <img src="../assets/search.svg" />
        </div>
        <div class="icon-button" @click="showFilter = true">
          <img id="noHover" src="../assets/filter.svg" />
          <img id="hover" src="../assets/filter-hover.svg" />
        </div>
        <button class="add-round-btn" @click="handleAddLead"><img src="../assets/plus-symbol.svg" />Add New</button>
      </div>
    </div>
    <div class="scrollable-div display-scrollbar" ref="scrollableDiv" @scroll="handleScroll">
      <div class="leads-table">
        <div class="table-row-header">
          <div></div>
          <div></div>
          <div style="cursor: pointer" @click="changeSort('NAME')">Name</div>
          <div style="cursor: pointer" @click="changeSort('CREATEDATE')">Date</div>
          <div style="cursor: pointer" @click="changeSort('SCORE')">Score</div>
          <div style="cursor: pointer" @click="changeSort('LAST Activity')">Last Activity</div>
          <div style="cursor: pointer" @click="changeSort('SOURCE')">Source</div>
          <div style="cursor: pointer" @click="changeSort('SALESPERSON')">Sales Person</div>
          <div>Contact</div>
          <div>Interaction</div>
          <div style="cursor: pointer" @click="changeSort('COMPLETE')">Complete</div>
          <div style="cursor: pointer" @click="changeSort('INVNUM')">Inv #</div>
          <div style="cursor: pointer" @click="changeSort('AMOUNT')">Amount</div>
          <div></div>
        </div>
        <template v-for="lead in visibleLeads" :key="lead.name">
          <LeadHeader
            :lead="lead"
            :isOpen="openLead == lead.LEADID"
            @toggleLead="toggleLead"
            @showAssignSalesperson="(leadId) => (showAssignSalesperson = leadId)"
            @showAddInteraction="(leadId) => (showAddInteraction = leadId)"
            @showAddCompletion="(leadId) => (showAddCompletion = leadId)"
            @showDeleteConfirmation="(leadId) => (showDeleteConfirmation = leadId)"
            @showUncompleteConfirmation="(leadId) => (showUncompleteConfirmation = leadId)"
            @showDelayAlert="(leadId) => (showDelayAlert = leadId)"
            @showUnlinkConfirmation="(leadId) => (showUnlinkConfirmation = leadId)"
            @showSendEmail="(templateId) => (showSendEmail = { lead, templateId })"
            @showSendContractorList="(lead) => (showSendContractorList = lead)"
            @showLinkContact="(leadId) => (showLinkContact = leadId)"
            @showMarkAsDead="(leadId) => (showMarkAsDeadConfirmation = leadId)" />
        </template>
        <div v-if="loadingLeads.value" class="table-row loading-row">
          <div class="leads-cell"><img src="../assets/spinner.svg" /></div>
        </div>
        <div v-if="!loadingLeads.value && !filteredLeads.length" class="table-row">
          <div class="leads-cell" style="width: 100%; padding-left: 5.2rem"><i>No Leads</i></div>
        </div>
      </div>
    </div>
    <div @click="openLead = null" v-show="openLead" ref="leadBackground" class="lead-background"></div>
  </div>
  <AddLead v-if="showAddLead && user.insideSales" @closePopup="showAddLead = false" />
  <AssignSalesperson v-if="showAssignSalesperson" :leadid="showAssignSalesperson" @closePopup="showAssignSalesperson = null" />
  <AddInteraction v-if="showAddInteraction" :leadid="showAddInteraction" @closePopup="showAddInteraction = null" />
  <AddCompletion v-if="showAddCompletion" :leadid="showAddCompletion" @closePopup="showAddCompletion = null" />
  <LeadFilter v-if="showFilter" @closePopup="showFilter = false" />
  <LinkContact v-if="showLinkContact" @closePopup="showLinkContact = false" :leadid="showLinkContact" />
  <Confirmation
    v-if="showUnlinkConfirmation"
    @confirm="unlinkContact(showUnlinkConfirmation)"
    @cancel="showUnlinkConfirmation = null"
    header="Unlink Contact"
    message="Are you sure you want to unlink the contact from this lead?"
    confirmText="Unlink"
    cancelText="Cancel"
    :serious="true" />
  <DelayAlert v-if="showDelayAlert" :leadid="showDelayAlert" @closePopup="showDelayAlert = null" />
  <Confirmation
    v-if="showDeleteConfirmation"
    @confirm="deleteLead(showDeleteConfirmation)"
    @cancel="showDeleteConfirmation = null"
    header="Delete Lead"
    message="Are you sure you want to delete this lead? this action cannot be undone."
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
  <Confirmation
    v-if="showUncompleteConfirmation"
    @confirm="uncompleteLead(showUncompleteConfirmation)"
    @cancel="showUncompleteConfirmation = null"
    header="Uncomplete Lead"
    message="Are you sure you want to uncomplete this lead?"
    confirmText="Uncomplete"
    cancelText="Cancel"
    :serious="true" />
  <Confirmation
    v-if="showMarkAsDeadConfirmation"
    @confirm="markLeadAsDead(showMarkAsDeadConfirmation)"
    @cancel="showMarkAsDeadConfirmation = null"
    header="Mark Lead as Dead"
    message="Are you sure you want to mark this lead as dead? This will change the score to dead and create a 'Marked as Dead' interaction."
    confirmText="Mark as Dead"
    cancelText="Cancel"
    :serious="true" />
  <SendEmail v-if="showSendEmail" :lead="showSendEmail.lead" :templateId="showSendEmail.templateId" @close="showSendEmail = null" />
  <SendContractorList v-if="showSendContractorList" :lead="showSendContractorList" @close="showSendContractorList = null" />
</template>

<script>
import AddLead from '../components/AddLead.vue';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import useSingleLeadQuery from '@/composables/useSingleLeadQuery';
import AssignSalesperson from '../components/AssignSalesperson.vue';
import AddInteraction from '../components/AddInteraction.vue';
import AddCompletion from '../components/AddCompletion.vue';
import LeadFilter from '../components/LeadFilter.vue';
import LinkContact from '../components/LinkContact.vue';
import Confirmation from '../components/Confirmation.vue';
import DelayAlert from '../components/DelayAlert.vue';
import useLeadMutate from '@/composables/useLeadMutate';
import useInteractionMutate from '@/composables/useInteractionMutate';
import LeadHeader from '../components/LeadHeader.vue';
import useAlerts from '@/composables/useAlerts';
import useCurrentUser from '@/composables/useCurrentUser';
import SendEmail from '@/components/SendEmail.vue';
import { onMounted, onBeforeUnmount, onUpdated } from 'vue';
import { getLastActivity } from '@/composables/useGetLastActivity';
import SendContractorList from '@/components/SendContractorList.vue';

export default {
  name: 'Leads',
  components: {
    AddLead,
    AssignSalesperson,
    AddInteraction,
    AddCompletion,
    LeadFilter,
    LinkContact,
    Confirmation,
    LeadHeader,
    DelayAlert,
    SendEmail,
    SendContractorList,
  },
  setup() {
    useLeadsQuery.useLeadsQuery();
    const { updateLead, deleteLead } = useLeadMutate();
    const { addInteraction } = useInteractionMutate();
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    let overFlowTimeout = null;
    const { allUsers } = useUsersQuery();

    const checkOverflow = () => {
      const leadStatusArrows = document.querySelectorAll('.lead-status-arrow');
      leadStatusArrows.forEach((arrow) => {
        const firstChildDiv = arrow.firstElementChild;
        if (!firstChildDiv) return;
        const mainElement = firstChildDiv.querySelector('.main');
        if (!mainElement) return;

        if (mainElement.scrollWidth > firstChildDiv.offsetWidth) {
          firstChildDiv.classList.add('text-overflow');
        } else {
          firstChildDiv.classList.remove('text-overflow');
        }
      });
    };

    const checkOverFlowDelay = () => {
      clearTimeout(overFlowTimeout);
      overFlowTimeout = setTimeout(checkOverflow, 100);
    };

    onMounted(() => {
      window.addEventListener('resize', checkOverFlowDelay);
      checkOverFlowDelay(); // Initial check
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', checkOverFlowDelay);
    });

    onUpdated(() => {
      checkOverFlowDelay();
    });

    return {
      leads: useLeadsQuery.leads,
      getLeads: useLeadsQuery.refetchLeads,
      allUsers,
      updateLead,
      deleteLead,
      addInteraction,
      addAlert,
      user,
      loadingLeads: useLeadsQuery.loadingLeads,
      filters: useLeadsQuery.filters,
      broadSearchExcludedSalespersonIds: useLeadsQuery.broadSearchExcludedSalespersonIds,
      getLastActivity,
      showSingleLead: useSingleLeadQuery.showSingleLead,
    };
  },
  data() {
    return {
      showAddLead: false,
      searchText: '',
      showAssignSalesperson: null,
      showAddInteraction: null,
      showAddCompletion: null,
      showFilter: false,
      showLinkContact: null,
      showUnlinkConfirmation: null,
      openLead: null,
      showDeleteConfirmation: null,
      showUncompleteConfirmation: null,
      showMarkAsDeadConfirmation: null,
      sortColumn: 'SCORE',
      sortDirection: 'desc',
      showDelayAlert: null,
      showSendEmail: null,
      showSendContractorList: null,
      maxRows: 100,
      rowIncrement: 50,
    };
  },
  computed: {
    filteredLeads() {
      const searchText = this.searchText.trim().toLowerCase();
      const filtered = this.leads.filter((lead) => {
        if (searchText && this.shouldExcludeLeadFromSearch(lead)) return false;

        let isMatch = false;
        if (this.getLeadName(lead).toLowerCase().includes(searchText)) isMatch = true;
        if (lead.DELIVERYADDRESS?.toLowerCase().includes(searchText)) isMatch = true;
        if (lead.JOBNAME?.toLowerCase().includes(searchText)) isMatch = true;
        if (lead.BWQUOTE?.toLowerCase().includes(searchText)) isMatch = true;
        if (lead.INVNUM?.toLowerCase().includes(searchText)) isMatch = true;
        if (lead.NOTES?.toLowerCase().includes(searchText)) isMatch = true;

        return isMatch;
      });

      const sorted = filtered.sort((a, b) => {
        if (this.sortColumn === 'NAME') {
          return this.getLeadName(a).localeCompare(this.getLeadName(b));
        } else if (this.sortColumn === 'LAST Activity') {
          const lastActivityA = this.getLastActivity(a);
          const lastActivityB = this.getLastActivity(b);
          const getActivityValue = (activity) => {
            if (activity === 'Today' || 'New') return 0;
            if (activity.includes('day')) {
              const days = parseInt(activity);
              return days;
            }
            return Number.MAX_SAFE_INTEGER; // For any other cases, sort to the end
          };

          const valueA = getActivityValue(lastActivityA);
          const valueB = getActivityValue(lastActivityB);

          if (valueA === valueB) {
            return lastActivityA.localeCompare(lastActivityB);
          }

          return valueA - valueB;
        } else if (this.sortColumn === 'SALESPERSON') {
          const salespersonA = this.allUsers.find((salesperson) => salesperson.USERID === a.ASSIGNEDID)?.DISPLAYNAME || '';
          const salespersonB = this.allUsers.find((salesperson) => salesperson.USERID === b.ASSIGNEDID)?.DISPLAYNAME || '';
          return salespersonA.localeCompare(salespersonB);
        } else if (this.sortColumn === 'COMPLETE') {
          return a.COMPLETE - b.COMPLETE;
        } else if (this.sortColumn === 'CREATEDATE') {
          return a.CREATEDATE - b.CREATEDATE;
        } else if (this.sortColumn === 'SOURCE') {
          return a.SOURCE.localeCompare(b.SOURCE);
        } else if (this.sortColumn === 'SCORE') {
          const getScoreValue = (score) => {
            if (score === 'hot') return 3;
            if (score === 'warm') return 2;
            if (score === 'cool') return 1;
            if (score === 'dead') return 0;
            return 4; // For any other cases, sort to the end
          };

          const scoreA = getScoreValue(a.SCORE);
          const scoreB = getScoreValue(b.SCORE);

          if (scoreA === scoreB) {
            // Secondary sort by create date
            return a.CREATEDATE - b.CREATEDATE;
          }

          return scoreA - scoreB;
        } else if (this.sortColumn === 'INVNUM') {
          if (!a.COMPLETE && !b.COMPLETE) return 0;
          if (!a.COMPLETE) return -1;
          if (!b.COMPLETE) return 1;
          let aNum = parseInt(a.INVNUM?.replace(/[^0-9]/g, '') || '0');
          let bNum = parseInt(b.INVNUM?.replace(/[^0-9]/g, '') || '0');
          return aNum - bNum;
        } else if (this.sortColumn === 'AMOUNT') {
          if (!a.COMPLETE && !b.COMPLETE) return 0;
          if (!a.COMPLETE) return -1;
          if (!b.COMPLETE) return 1;
          return a.AMOUNT - b.AMOUNT;
        }
      });

      if (this.sortDirection === 'desc') {
        return sorted.reverse();
      } else {
        return sorted;
      }
    },
    visibleLeads() {
      return this.filteredLeads.slice(0, this.maxRows);
    },
  },
  methods: {
    normalizeSalespersonId(salespersonId) {
      return salespersonId?.toString().toUpperCase();
    },
    isSelectedBroadSearchExcludedSalesperson() {
      return this.broadSearchExcludedSalespersonIds.has(this.normalizeSalespersonId(this.filters.salespersonId));
    },
    shouldExcludeLeadFromSearch(lead) {
      if (this.isSelectedBroadSearchExcludedSalesperson()) return false;
      return this.broadSearchExcludedSalespersonIds.has(this.normalizeSalespersonId(lead.ASSIGNEDID));
    },
    getLeadName(lead) {
      if (lead.company) {
        return lead.company.NAME;
      } else if (lead.contact) {
        return `${lead.contact.FIRSTNAME} ${lead.contact.LASTNAME}`;
      } else {
        return lead.NAME || '';
      }
    },
    async unlinkContact(leadid) {
      const lead = this.leads.find((lead) => lead.LEADID === leadid);
      lead.company = null;
      lead.contact = null;
      lead.LINKMETHOD = null;
      this.showUnlinkConfirmation = null;

      const leadToUpdate = {
        COMPANYID: null,
        CONTACTID: null,
        LINKMETHOD: null,
      };

      await this.updateLead(leadid, leadToUpdate);
    },
    async uncompleteLead(leadid) {
      const lead = this.leads.find((lead) => lead.LEADID === leadid);
      lead.COMPLETE = false;
      lead.COMPLETEDATE = null;
      this.showUncompleteConfirmation = null;
      const leadToUpdate = {
        COMPLETE: false,
        COMPLETEDATE: null,
      };
      await this.updateLead(leadid, leadToUpdate);
    },
    toggleLead(leadid) {
      if (this.openLead == leadid) {
        this.openLead = null;
      } else if (this.openLead == null) {
        this.openLead = leadid;
      }
    },
    async deleteLead(leadid) {
      this.showDeleteConfirmation = null;

      try {
        await this.deleteLead(leadid);
      } catch (error) {
        console.error(error);
      }

      const leadIndex = this.leads.findIndex((lead) => lead.LEADID == leadid);
      if (leadIndex !== -1) this.leads.splice(leadIndex, 1);
      this.addAlert('Deleted Lead', 'low', 3);
      this.openLead = null;
    },
    async markLeadAsDead(leadid) {
      this.showMarkAsDeadConfirmation = null;

      try {
        // Find the lead to update
        const lead = this.leads.find((lead) => lead.LEADID === leadid);
        if (!lead) {
          this.addAlert('Error finding lead', 'high', 4);
          return;
        }

        // Update the lead's score to 'dead'
        const leadToUpdate = {
          SCORE: 'dead',
        };

        // Create a new interaction
        const newInteraction = {
          LEADID: leadid,
          NOTE: 'Marked as dead.',
          TYPE: 'Marked as Dead',
          DATE: Date.now().toString(),
          SALESPERSONID: this.user.userid,
        };

        const newInteractionResponse = await this.addInteraction(newInteraction);

        // Update the local lead object
        lead.SCORE = 'dead';

        // Add the interaction to the lead's interactions array
        if (newInteractionResponse && newInteractionResponse.addInteraction) {
          newInteraction.INTERACTIONID = newInteractionResponse.addInteraction;
          lead.interactions.push(newInteraction);
        }

        await this.updateLead(leadid, leadToUpdate);

        this.addAlert('Lead marked as dead', 'low', 3);
      } catch (error) {
        console.error('Error marking lead as dead:', error);
        this.addAlert('Error marking lead as dead', 'high', 4);
      }
    },

    changeSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'desc';
      }
    },
    updateFilters() {
      this.filters.searchAllSalespeople = this.searchText.trim() !== '';
      this.maxRows = 100;
    },
    handleScroll(event) {
      const element = event.target;
      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight;
      const clientHeight = element.clientHeight;
      const nearBottom = scrollTop + clientHeight >= scrollHeight - 100;
      if (nearBottom && this.maxRows < this.filteredLeads.length && !this.loadingLeads.value) {
        this.maxRows += this.rowIncrement;
      }
    },
    handleAddLead() {
      if (this.user.insideSales) {
        this.showAddLead = true;
      } else {
        this.addAlert('You are not an inside salesperson and cannot add leads', 'high', 4);
      }
    },
  },
  mounted() {
    this.getLeads();
    this.$refs.leadBackground.style.height = `${this.$refs.mainBody.scrollHeight}px`;
    this.maxRows = 100;

    if (this.$route.params.leadid) {
      this.showSingleLead = this.$route.params.leadid;
    }
  },
  updated() {
    this.$refs.leadBackground.style.height = `${this.$refs.mainBody.scrollHeight}px`;
  },
  watch: {
    filteredLeads() {
      if (this.openLead) {
        const exists = this.filteredLeads.some((lead) => lead.LEADID === this.openLead);
        if (!exists) {
          this.openLead = null;
        }
      }
    },
    '$route.params.leadid'(newVal) {
      if (newVal && newVal !== this.showSingleLead) {
        this.showSingleLead = newVal;
      } else if (!newVal) {
        this.showSingleLead = null;
      }
    },
    showSingleLead(newVal) {
      if (this.$route.name !== 'leads') return;
      if (newVal) {
        this.$router.replace({ name: 'leads', params: { leadid: newVal } });
      } else {
        this.$router.replace({ name: 'leads' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.main-body {
  position: relative;
  overflow-x: hidden;

  .lead-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.08);
    z-index: 1;
  }
}

.scrollable-div {
  position: relative;
  overflow: auto;
  height: calc(100vh - 140px);
}
</style>
